import {AssetType, isProductionMode} from '@hconnect/common/types'
import type {AssetPayload, Asset, OperationModePayload} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {NumericTextField} from '@hconnect/uikit/src/lib2'
import {TextField, Stack} from '@mui/material'
import {useForm, Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {requiredValidator, submitOnBlurAndEnterProps} from '../../../common/utils'
import {useUrlParam} from '../../../routing'

import {useEditAsset, useEditOperationMode} from '@settings/modules/assets'

type BaseloadFormState = Pick<OperationModePayload, 'powerConsumption'> & Pick<AssetPayload, 'name'>

interface BaseLoadFormProps {
  baseLoadAsset: Asset
  isReadOnly: boolean
}

export const BaseLoadForm: React.FC<BaseLoadFormProps> = ({baseLoadAsset, isReadOnly}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')

  const {mutate: editAsset} = useEditAsset()
  const {mutate: editAssetOperationMode} = useEditOperationMode()

  // Base load must have only one production operation mode
  const operationMode = baseLoadAsset.operationModes.find(isProductionMode)!

  const {id: baseloadOperationModeId, ...baseloadOperationModeBase} = operationMode

  const initialValues: BaseloadFormState = {
    name: baseLoadAsset.name,
    powerConsumption: baseloadOperationModeBase.powerConsumption
  }

  const {
    control,
    reset,
    handleSubmit,
    formState: {dirtyFields, isDirty}
  } = useForm<BaseloadFormState>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: initialValues
  })

  const submit = handleSubmit(({name, powerConsumption}) => {
    if (!isDirty) return

    const onError = () => reset()
    const onSuccess = () => reset({name, powerConsumption})

    if (dirtyFields.name) {
      return editAsset(
        {plantCode, assetId: baseLoadAsset.id, key: 'name', dto: {name, type: AssetType.BaseLoad}},
        {onError, onSuccess}
      )
    }

    editAssetOperationMode(
      {
        plantCode,
        assetId: baseLoadAsset.id,
        operationModeId: baseloadOperationModeId,
        key: 'powerConsumption',
        dto: {...baseloadOperationModeBase, powerConsumption}
      },
      {onError, onSuccess}
    )
  })

  return (
    <Stack spacing={2} {...dataTestId('edit_baseload_form')}>
      <Controller
        control={control}
        name="name"
        rules={requiredValidator(t)}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <TextField
            label={t('assetsSettings.assetName')}
            sx={{width: ({spacing}) => spacing(50)}}
            value={value}
            placeholder={value}
            inputRef={ref}
            onChange={onChange}
            helperText={error?.message}
            disabled={isReadOnly}
            error={Boolean(error?.message)}
            {...submitOnBlurAndEnterProps(submit)}
            {...dataTestId('baseload_name_input')}
          />
        )}
      />
      <Controller
        control={control}
        name="powerConsumption"
        rules={requiredValidator(t)}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <NumericTextField
            sx={{width: ({spacing}) => spacing(18)}}
            label={t('assetsSettings.powerConsumption')}
            inputRef={ref}
            onChange={({target: {value}}) => onChange(value === '' ? '' : Number(value))}
            value={value}
            disabled={isReadOnly}
            error={!!error}
            helperText={error?.message}
            {...submitOnBlurAndEnterProps(submit)}
            {...dataTestId('baseload_power_consumption_input')}
          />
        )}
      />
    </Stack>
  )
}
